import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { useForm } from 'react-typed-form';
import { FieldTextRequired, FieldMediaUpload } from '../../form';
import ConfirmModal from '../../components/ConfirmModal';
import { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { TrashSimple } from '@phosphor-icons/react';
import { Api, useApiFormSubmit, useApiCallable } from '../../api';
import { toastErrorHandler } from '../../util';
import { iriToId } from '../../util';

type Props = {
  existing?: Api.Organisation.V_DetailAsAdmin | null;
};

export default function B2BOrganisationForm({ existing }: Props) {
  const navigate = useNavigate();
  const callApi = useApiCallable();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const initialValues = {
    name: existing?.name || '',
    licencePrefix: existing?.licencePrefix || '',
    logo: existing?.logo || null,
  };

  const form = useForm<Api.Organisation.W>({
    defaultValues: initialValues,
    onSubmit: useApiFormSubmit(
      existing ? 'PATCH' : 'POST',
      existing ? `/organisations/${existing.id}` : '/organisations',
      {
        onSuccess: () => {
          toast.success(
            existing ? 'Organisation updated' : 'Organisation created',
          );
          navigate(existing ? `/b2b/${existing.id}` : '/b2b');
        },
      },
    ),
  });

  const handleDelete = async () => {
    if (!existing) return;
    const res = await callApi(
      `/organisations/${existing.id}`,
      { method: 'DELETE' },
      { errorHandler: toastErrorHandler },
    );
    if (res) {
      setShowDeleteModal(false);
      toast.success('Organisation deleted');
      navigate('/b2b');
    }
  };

  return (
    <Stack gap={3}>
      <Box maxWidth={700}>
        <FieldTextRequired
          field={form.getField('name')}
          label="Name"
          required
        />
      </Box>
      <Box maxWidth={700}>
        <FieldTextRequired
          field={form.getField('licencePrefix')}
          label="Licence Prefix"
          helperText="This will appear at the beginning of each license code"
          required
        />
      </Box>
      <Box maxWidth={700}>
        <FieldMediaUpload
          field={form.getField('logo')}
          label="Company Logo"
          acceptedTypes={['IMAGE_TYPE']}
          helperText="This will appear in the app for all users of this company"
          existingImageId={existing?.logo ? iriToId(existing.logo) : null}
        />
      </Box>
      <Divider />
      <Stack direction="row" gap={2} justifyContent="space-between">
        <Box>
          <Button variant="contained" onClick={form.handleSubmit}>
            {existing ? 'Save Changes' : 'Create Organisation'}
          </Button>
        </Box>
        {existing && (
          <Stack alignItems="flex-end" gap={0.5}>
            <Button
              variant="contained"
              color="error"
              onClick={() => setShowDeleteModal(true)}
              startIcon={<TrashSimple />}
            >
              Delete
            </Button>
            <Typography variant="body2" color="text.secondary">
              Deleting an organisation will remove all associated data
            </Typography>
          </Stack>
        )}
      </Stack>
      {showDeleteModal && (
        <ConfirmModal
          title="Delete Organisation"
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          color="error"
          confirmText="Delete"
        >
          Are you sure you want to delete this organisation? This action cannot
          be undone and will remove all associated licenses and data.
        </ConfirmModal>
      )}
    </Stack>
  );
}
